import React, { useState, useEffect } from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Search, ChevronDown, ChevronUp, X } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";

export default function ObjectiveSearch({ objectives, missions, officers, onFilteredResults }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [missionFilter, setMissionFilter] = useState('all');
  const [ownerFilter, setOwnerFilter] = useState('all');
  const [bridgeScoreRange, setBridgeScoreRange] = useState([0, 100]);
  const [smartScoreRange, setSmartScoreRange] = useState([0, 100]);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  useEffect(() => {
    let filtered = objectives;

    // Search in statement, specific, measurable, attainable, relevant, time_bound
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((obj) =>
        obj.statement?.toLowerCase().includes(query) ||
        obj.specific?.toLowerCase().includes(query) ||
        obj.measurable?.toLowerCase().includes(query) ||
        obj.attainable?.toLowerCase().includes(query) ||
        obj.relevant?.toLowerCase().includes(query) ||
        obj.time_bound?.toLowerCase().includes(query)
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter((obj) => obj.status === statusFilter);
    }

    // Mission filter
    if (missionFilter !== 'all') {
      filtered = filtered.filter((obj) => obj.mission_id === missionFilter);
    }

    // Owner filter
    if (ownerFilter !== 'all') {
      filtered = filtered.filter((obj) => obj.owner_email === ownerFilter);
    }

    // Bridge quality score filter
    filtered = filtered.filter((obj) => {
      if (obj.bridge_quality_score === undefined) return true;
      return obj.bridge_quality_score >= bridgeScoreRange[0] && obj.bridge_quality_score <= bridgeScoreRange[1];
    });

    // SMART completeness score filter
    filtered = filtered.filter((obj) => {
      if (obj.smart_completeness_score === undefined) return true;
      return obj.smart_completeness_score >= smartScoreRange[0] && obj.smart_completeness_score <= smartScoreRange[1];
    });

    onFilteredResults(filtered);
  }, [searchQuery, statusFilter, missionFilter, ownerFilter, bridgeScoreRange, smartScoreRange, objectives]);

  const clearFilters = () => {
    setSearchQuery('');
    setStatusFilter('all');
    setMissionFilter('all');
    setOwnerFilter('all');
    setBridgeScoreRange([0, 100]);
    setSmartScoreRange([0, 100]);
  };

  const hasActiveFilters = searchQuery || statusFilter !== 'all' || missionFilter !== 'all' || 
    ownerFilter !== 'all' || bridgeScoreRange[0] > 0 || bridgeScoreRange[1] < 100 || 
    smartScoreRange[0] > 0 || smartScoreRange[1] < 100;

  return (
    <Card className="glass-panel border-none mb-6">
      <CardContent className="p-4">
        <div className="space-y-4">
          {/* Search Bar */}
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <Input data-testid="disc-objectivesearch-input"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search objectives by statement or SMART components..."
                className="bg-slate-950 border-slate-800 pl-10"
              />
            </div>
            <Button
              variant="outline"
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              className="border-slate-700 text-slate-300">
              {showAdvancedFilters ? <ChevronUp className="w-4 h-4 mr-2" /> : <ChevronDown className="w-4 h-4 mr-2" />}
              Filters
            </Button>
            {hasActiveFilters && (
              <Button
                variant="ghost"
                onClick={clearFilters}
                className="text-slate-400 hover:text-slate-200">
                <X className="w-4 h-4 mr-2" />
                Clear
              </Button>
            )}
          </div>

          {/* Advanced Filters */}
          {showAdvancedFilters && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t border-slate-800">
              <div className="space-y-2">
                <Label className="text-slate-400 text-xs">Status</Label>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="bg-slate-950 border-slate-700 text-slate-100 h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="drafting">Drafting</SelectItem>
                    <SelectItem value="pending_activation">Pending Activation</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="archived">Archived</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-slate-400 text-xs">Mission</Label>
                <Select value={missionFilter} onValueChange={setMissionFilter}>
                  <SelectTrigger className="bg-slate-950 border-slate-700 text-slate-100 h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Missions</SelectItem>
                    {missions.map((mission) => (
                      <SelectItem key={mission.id} value={mission.id}>
                        {mission.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-slate-400 text-xs">Owner</Label>
                <Select value={ownerFilter} onValueChange={setOwnerFilter}>
                  <SelectTrigger className="bg-slate-950 border-slate-700 text-slate-100 h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Officers</SelectItem>
                    {officers.map((officer) => (
                      <SelectItem key={officer.id} value={officer.user_email}>
                        {officer.callsign}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-slate-400 text-xs">Bridge Quality: {bridgeScoreRange[0]}% - {bridgeScoreRange[1]}%</Label>
                <Slider
                  min={0}
                  max={100}
                  step={5}
                  value={bridgeScoreRange}
                  onValueChange={setBridgeScoreRange}
                  className="mt-2"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-slate-400 text-xs">SMART Completeness: {smartScoreRange[0]}% - {smartScoreRange[1]}%</Label>
                <Slider
                  min={0}
                  max={100}
                  step={5}
                  value={smartScoreRange}
                  onValueChange={setSmartScoreRange}
                  className="mt-2"
                />
              </div>
            </div>
          )}

          {/* Active Filters Display */}
          {hasActiveFilters && (
            <div className="flex flex-wrap gap-2 pt-2">
              {searchQuery && (
                <Badge variant="outline" className="border-indigo-500/30 text-indigo-400">
                  Search: "{searchQuery}"
                </Badge>
              )}
              {statusFilter !== 'all' && (
                <Badge variant="outline" className="border-emerald-500/30 text-emerald-400">
                  Status: {statusFilter}
                </Badge>
              )}
              {missionFilter !== 'all' && (
                <Badge variant="outline" className="border-cyan-500/30 text-cyan-400">
                  Mission: {missions.find(m => m.id === missionFilter)?.title}
                </Badge>
              )}
              {ownerFilter !== 'all' && (
                <Badge variant="outline" className="border-purple-500/30 text-purple-400">
                  Owner: {officers.find(o => o.user_email === ownerFilter)?.callsign}
                </Badge>
              )}
              {(bridgeScoreRange[0] > 0 || bridgeScoreRange[1] < 100) && (
                <Badge variant="outline" className="border-amber-500/30 text-amber-400">
                  Bridge: {bridgeScoreRange[0]}-{bridgeScoreRange[1]}%
                </Badge>
              )}
              {(smartScoreRange[0] > 0 || smartScoreRange[1] < 100) && (
                <Badge variant="outline" className="border-rose-500/30 text-rose-400">
                  SMART: {smartScoreRange[0]}-{smartScoreRange[1]}%
                </Badge>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}